// HTTP helpers for the BFF session endpoints. The SPA never sees a token —
// authentication is by the HttpOnly `pb_session` cookie. We do hold the
// CSRF token in memory: the BFF returns it on /me and /callback responses;
// the SPA mirrors it into the `X-CSRF-Token` header on every state-changing
// request.
//
// All requests use `credentials: 'include'` so the browser sends the
// session cookie automatically.

import type {
  SecureCollabLifecycleEventV1,
  SecureTicketOutcomeV1,
} from "@playbookai/collab-client";
import type { CollabTicketRequestV1 } from "@playbookai/types";
import {
  forgetPasswordChangeMarker,
  forgetReturnTo,
  keepReturnTo,
} from "./auth-tab-storage.js";
import { clearSignInLease, recordSignInFlow } from "./sign-in-lease.js";

export interface SessionClaims {
  readonly userId: string;
  readonly tenantId: string;
  /** Unix seconds when the current access_token expires. The BFF refreshes silently. */
  readonly expiresAt: number;
  /** Email from the verified id_token, or `null` when absent (PB-5). */
  readonly email: string | null;
  /** Display name (`name` ?? `preferred_username`), or `null` when absent (PB-5). */
  readonly name: string | null;
  /** Tenant display name for the shell chrome. `null` when the BFF omits it. */
  readonly tenantName: string | null;
  /** Optional compact tenant mark for the shell chrome. */
  readonly tenantShortName?: string | null;
  /** Realm roles from the access token's `realm_access.roles` (PB-5). `[]` when
   * the user has none; absent only from a pre-PB-5 BFF, where UI gates fail closed. */
  readonly roles?: readonly string[];
  readonly enabledModules: readonly string[];
  /** Opaque server session generation used to fence secure collaboration. */
  readonly authSessionGeneration: string;
  /**
   * How long the session cookie survives without activity. The SPA cannot read
   * an HttpOnly cookie's max-age, so the keep-alive has no way to pace itself
   * unless the server declares it. Optional: a BFF that predates this field
   * leaves the keep-alive on its conservative default.
   */
  readonly sessionIdleTtlSeconds?: number;
  /**
   * How this person authenticates. OPTIONAL and never null: a BFF that predates
   * this field, or a session minted before it shipped, omits it — and the
   * absence means UNKNOWN, not "no". Account settings shows its password
   * section only on an explicit `local`, so treating absent as either value
   * would make that gate guess (PB-5415).
   */
  readonly authenticationMethod?: "local" | "microsoft-entra";
  /** Whether this deployment currently offers self-service password changes. */
  readonly passwordChangeSelfServiceEnabled?: boolean;
}

const BASE = ""; // BFF lives same-origin (or behind ingress at the same hostname)

let csrfToken: string | null = null;

export class AuthCallbackError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "AuthCallbackError";
    this.status = status;
    this.code = code;
  }
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

function setCsrfToken(value: string | null): void {
  csrfToken = value;
}

/**
 * How long one /me attempt may hang before it is abandoned.
 *
 * `fetch` has no timeout of its own, and a request that is never answered is
 * indistinguishable from one that will be answered eventually. Without this, a
 * wedged BFF — connection accepted, no response — reproduces PB-5206's symptom
 * through a path the retry ladder cannot reach: the query stays `pending` for
 * ever, so neither a retry nor the `unavailable` screen ever runs.
 *
 * 5s is far above a healthy /me (a Redis read). With three attempts and the
 * 0.5s and 1s gaps between them, the worst wait before the recovery screen is
 * about 16.5s (PB-5461).
 */
export const ME_REQUEST_TIMEOUT_MS = 5_000;

export interface FetchMeOptions {
  /**
   * Cancellation from the caller. `queryFn: fetchMe` needs no wiring for this —
   * TanStack Query calls the query function with a context object that already
   * carries `signal`, so the destructure below picks it up as-is.
   */
  readonly signal?: AbortSignal;
}

/** Fetch the current session's claims. Returns null on 401 (unauthenticated). */
export async function fetchMe(
  options: FetchMeOptions = {},
): Promise<SessionClaims | null> {
  const caller = options.signal;
  const controller = new AbortController();
  const abortFromCaller = (): void => controller.abort(caller?.reason);
  // Checked before subscribing: an ALREADY-aborted signal never fires `abort`
  // again, so a listener alone would miss it and issue the request anyway.
  if (caller?.aborted) abortFromCaller();
  else caller?.addEventListener("abort", abortFromCaller, { once: true });
  const timeout = setTimeout(() => {
    controller.abort(
      new Error(`fetchMe: no response within ${ME_REQUEST_TIMEOUT_MS}ms`),
    );
  }, ME_REQUEST_TIMEOUT_MS);

  try {
    return await requestMe(controller.signal);
  } finally {
    // Both are required. Leaving the timer pending keeps the process awake in
    // tests and fires an abort on an already-settled request; leaving the
    // listener attached leaks one per call onto a signal that outlives them.
    clearTimeout(timeout);
    caller?.removeEventListener("abort", abortFromCaller);
  }
}

async function requestMe(signal: AbortSignal): Promise<SessionClaims | null> {
  const r = await fetch(`${BASE}/api/auth/me`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    signal,
  });
  if (r.status === 401) {
    setCsrfToken(null);
    return null;
  }
  if (!r.ok) {
    throw new Error(`fetchMe: unexpected ${r.status} ${r.statusText}`);
  }
  const body = (await r.json()) as SessionClaims & {
    csrfToken?: string;
    roles?: unknown;
    authenticationMethod?: unknown;
    passwordChangeSelfServiceEnabled?: unknown;
    email?: unknown;
    name?: unknown;
    tenantName?: unknown;
    tenantShortName?: unknown;
    enabledModules?: unknown;
    authSessionGeneration?: unknown;
  };
  if (typeof body.csrfToken === "string") setCsrfToken(body.csrfToken);
  // Forward roles only as a validated string array (PB-5). Absent (a pre-PB-5
  // BFF) is left undefined so UI gates fail closed on the default-deny path.
  const roles = Array.isArray(body.roles)
    ? body.roles.filter((role): role is string => typeof role === "string")
    : undefined;
  if (
    typeof body.authSessionGeneration !== "string" ||
    body.authSessionGeneration.trim().length === 0
  ) {
    throw new Error("fetchMe: secure session generation unavailable");
  }
  return {
    userId: body.userId,
    tenantId: body.tenantId,
    expiresAt: body.expiresAt,
    email: typeof body.email === "string" ? body.email : null,
    name: typeof body.name === "string" ? body.name : null,
    // Fail-soft, unlike authSessionGeneration above: the tenant name is display
    // chrome, so an absent or malformed value degrades to a fallback rather than
    // blocking the session.
    tenantName: typeof body.tenantName === "string" ? body.tenantName : null,
    tenantShortName:
      typeof body.tenantShortName === "string" ? body.tenantShortName : null,
    ...(roles ? { roles } : {}),
    enabledModules: Array.isArray(body.enabledModules)
      ? body.enabledModules.filter(
          (module): module is string => typeof module === "string",
        )
      : [],
    authSessionGeneration: body.authSessionGeneration,
    ...(typeof body.sessionIdleTtlSeconds === "number" &&
    Number.isFinite(body.sessionIdleTtlSeconds) &&
    body.sessionIdleTtlSeconds > 0
      ? { sessionIdleTtlSeconds: body.sessionIdleTtlSeconds }
      : {}),
    // Only the two known values pass. Anything else — absent, null, or a value
    // from a later vocabulary — is left off, and the UI reads that as unknown.
    ...(body.authenticationMethod === "local" ||
    body.authenticationMethod === "microsoft-entra"
      ? { authenticationMethod: body.authenticationMethod }
      : {}),
    // Fail closed against an older/malformed BFF: the notice still uses the
    // authentication method, but the credential-writing button stays hidden.
    passwordChangeSelfServiceEnabled:
      body.passwordChangeSelfServiceEnabled === true,
  };
}

/**
 * What `/api/auth/callback` reported. Two flows return through it, because the
 * confidential BFF client has one registered redirect URI in every environment
 * (PB-5415). A completed sign-in carries no `outcome`.
 */
export interface CallbackResult {
  readonly returnTo: string;
  readonly outcome?: "password-changed" | "password-change-cancelled";
  /**
   * Whether revoking every session actually finished. Absent on a sign-in.
   * Read as `incomplete` when the server reports anything other than the exact
   * string `revoked`, so a missing or unrecognised value never becomes a
   * promise that the sign-out succeeded (PB-5415).
   */
  readonly signOut?: "revoked" | "incomplete";
}

export interface BeginLoginOptions {
  /**
   * `login` forces Keycloak's login form and, once the new sign-in is saved,
   * ends this browser's app session: "Choose another account" (PR 391).
   * `select_account` asks the upstream to let the user choose an account.
   */
  readonly prompt?: "login" | "select_account" | undefined;
}

/**
 * A sign-in that could not be started. The message is for logs only and never
 * reaches the screen; callers read `status` and `retryAfterSeconds`.
 */
export class BeginLoginError extends Error {
  /** HTTP status, or 0 when no answer arrived (network, aborted). */
  readonly status: number;
  /** The server's `Retry-After`, in seconds, when it sent one. */
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    retryAfterSeconds: number | null,
    message: string,
  ) {
    super(message);
    this.name = "BeginLoginError";
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * How long one request of a sign-in start may hang, reading its answer's body
 * included, before it is abandoned (PB-5461, PR 407 review F2).
 *
 * Every start now reads the CSRF token first, and `fetch` has no timeout of its
 * own: a BFF that accepts that read and never answers left the page on
 * "Redirecting to sign in…" for ever, because the start never settled and so
 * never reached Try again. A start makes at most four requests — token, login,
 * and the one 403 retry of each — so it gives up within about 20s at worst, and
 * within 5s when the first request is the silent one. 5s is far above a healthy
 * answer (a Redis read or write), as for {@link ME_REQUEST_TIMEOUT_MS}.
 */
export const LOGIN_REQUEST_TIMEOUT_MS = 5_000;

/**
 * Begin the OIDC redirect via the BFF. Returns the URL the SPA must navigate to.
 *
 * Every sign-in start, from every caller (page guard, return page, the Player
 * AI "Sign in" card), first reads the CURRENT session's CSRF token (PB-5461).
 * A tab can hold no token, or a stale one, while the browser has a live
 * session — another tab signed in, or the session rotated — and the BFF then
 * refuses the start with 403. A 403 is answered by reading the token again and
 * trying once more; never more, so a real refusal cannot loop. Each request is
 * abandoned after {@link LOGIN_REQUEST_TIMEOUT_MS}, which fails the start with
 * a `BeginLoginError` of status 0, as a dropped connection does.
 *
 * The answer's address names the flow the start opened (its OIDC `state`); a
 * page holding the one-tab sign-in lease records it there, so only that flow's
 * callback owns the lease afterwards (PR 407 review round 2 F1).
 *
 * Two tab records move with every start: the page it started from is kept for
 * the return page's automatic restart, and any password-change marker is
 * cleared, because a new sign-in supersedes an abandoned change (AC-21).
 */
export async function beginLogin(
  returnTo: string,
  options: BeginLoginOptions = {},
): Promise<string> {
  keepReturnTo(returnTo);
  forgetPasswordChangeMarker();
  const query = new URLSearchParams({ returnTo });
  if (options.prompt) query.set("prompt", options.prompt);
  const url = `${BASE}/api/auth/login?${query.toString()}`;

  await loadCsrfTokenForLogin();
  let answer = await postLogin(url);
  if (answer.response.status === 403) {
    await loadCsrfTokenForLogin();
    answer = await postLogin(url);
  }
  const { response, body } = answer;
  if (!response.ok) throw beginLoginFailure(response);
  const address = (body as { url?: unknown } | null)?.url;
  if (typeof address !== "string" || address.length === 0) {
    throw new BeginLoginError(
      response.status,
      null,
      "beginLogin: no URL in answer",
    );
  }
  recordSignInFlow(address);
  return address;
}

/**
 * Run one request of a sign-in start, its body read included, under its own
 * {@link LOGIN_REQUEST_TIMEOUT_MS} cut-off. Anything but a `BeginLoginError` —
 * a dropped connection, the cut-off — becomes one with status 0.
 */
async function withinLoginTimeout<T>(
  label: "csrf" | "login",
  request: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort(
      new Error(`no response within ${LOGIN_REQUEST_TIMEOUT_MS}ms`),
    );
  }, LOGIN_REQUEST_TIMEOUT_MS);
  try {
    return await request(controller.signal);
  } catch (cause) {
    if (cause instanceof BeginLoginError) throw cause;
    throw new BeginLoginError(0, null, `beginLogin: ${label} ${String(cause)}`);
  } finally {
    clearTimeout(timeout);
  }
}

/** POST the start. A 2xx answer's body is read inside the same cut-off;
 *  any other answer is returned unread, for the caller to judge. */
function postLogin(
  url: string,
): Promise<{ readonly response: Response; readonly body: unknown }> {
  return withinLoginTimeout("login", async (signal) => {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      ...(csrfToken ? { headers: { "X-CSRF-Token": csrfToken } } : {}),
      signal,
    });
    if (!response.ok) return { response, body: null };
    try {
      return { response, body: (await response.json()) as unknown };
    } catch (cause) {
      // The cut-off is no answer at all, not an unreadable one.
      if (signal.aborted) throw cause;
      throw new BeginLoginError(
        response.status,
        null,
        "beginLogin: unreadable body",
      );
    }
  });
}

/** Read the token a sign-in start needs; a failure to read it fails the start. */
async function loadCsrfTokenForLogin(): Promise<void> {
  const r = await withinLoginTimeout("csrf", requestCsrfToken);
  if (!r.ok && r.status !== 401) throw beginLoginFailure(r);
}

function beginLoginFailure(r: Response): BeginLoginError {
  return new BeginLoginError(
    r.status,
    retryAfterSeconds(r.headers?.get("Retry-After") ?? null),
    `beginLogin: ${r.status} ${r.statusText}`,
  );
}

/**
 * `GET /api/auth/csrf-token`: 200 carries the live session's token, 401 means
 * there is no session and so no token. Sets the in-memory token from either
 * answer and returns the response for the caller to judge anything else.
 * `signal` abandons the request and its body read alike.
 */
async function requestCsrfToken(signal?: AbortSignal): Promise<Response> {
  const r = await fetch(`${BASE}/api/auth/csrf-token`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    ...(signal ? { signal } : {}),
  });
  if (r.status === 401) {
    setCsrfToken(null);
  } else if (r.ok) {
    let token: unknown;
    try {
      token = ((await r.json()) as { csrfToken?: unknown }).csrfToken;
    } catch (cause) {
      // Abandoned, not unreadable: the caller fails rather than going on.
      if (signal?.aborted) throw cause;
      token = null;
    }
    setCsrfToken(typeof token === "string" && token.length > 0 ? token : null);
  }
  return r;
}

/**
 * Complete the OIDC callback. The BFF has already extracted code+state from
 * the URL via its own /api/auth/callback handler — this is the SPA-facing
 * read of the result.
 *
 * The SPA's /auth/callback route makes this request immediately on mount,
 * cookies-included; the BFF inspects the request's URL for the OIDC params,
 * mints a session cookie, and returns {returnTo, csrfToken}.
 */
export async function completeCallback(
  callbackQueryString: string,
): Promise<CallbackResult> {
  const r = await fetch(`${BASE}/api/auth/callback${callbackQueryString}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw await parseCallbackError(r);
  const body = (await r.json()) as {
    returnTo: string;
    csrfToken?: string;
    outcome?: unknown;
    signOut?: unknown;
  };
  if (typeof body.csrfToken === "string") setCsrfToken(body.csrfToken);
  /* Only the two known outcomes are carried through. A sign-in has never
     reported one, so an absent value stays a sign-in — which is also what an
     older BFF answers (PB-5415). */
  const outcome =
    body.outcome === "password-changed" ||
    body.outcome === "password-change-cancelled"
      ? body.outcome
      : undefined;
  if (outcome === "password-changed") {
    /* Every session is gone, this browser's included. Dropping the token stops
       any in-flight request from presenting one that can no longer be honoured. */
    setCsrfToken(null);
  }
  return {
    returnTo: body.returnTo,
    ...(outcome ? { outcome } : {}),
    // Fail LOUD, not silent: only an explicit `revoked` is treated as a
    // completed sign-out, so an older BFF or a malformed value degrades to the
    // cautious message rather than to the reassuring one.
    ...(outcome
      ? { signOut: body.signOut === "revoked" ? "revoked" : "incomplete" }
      : {}),
  } as const;
}

async function parseCallbackError(
  response: Response,
): Promise<AuthCallbackError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const envelope =
    typeof body === "object" && body !== null && !Array.isArray(body)
      ? (body as { code?: unknown; message?: unknown })
      : null;
  const code =
    typeof envelope?.code === "string" && envelope.code.length > 0
      ? envelope.code
      : "auth_callback_failed";
  const message =
    typeof envelope?.message === "string" && envelope.message.length > 0
      ? envelope.message
      : "Could not complete sign-in.";
  return new AuthCallbackError(response.status, code, message);
}

/**
 * Why a silent renew failed. Only a definitive rejection may end the session
 * (PB-4900).
 *
 *  - `terminal`  the BFF answered 401: this session is gone. Sign the user out.
 *  - `transient` no verdict was received — a 403 (stale CSRF token), a 5xx, or
 *                the network. The session may be perfectly valid, so it must be
 *                left alone. Treating "could not ask" as "the answer was no"
 *                would let one auth-service blip sign out every user at once.
 *
 * Mirrors the OAuth 2.0 split (RFC 6749 §5.2), where `invalid_grant` — not a
 * transport failure — is what triggers re-authentication.
 */
export type RefreshFailureKind = "terminal" | "transient";

export class RefreshSessionError extends Error {
  constructor(
    readonly kind: RefreshFailureKind,
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "RefreshSessionError";
  }
}

/**
 * Single-flight latch. Several requests failing at once must produce ONE renew,
 * not one per request — but each caller still awaits the real outcome, because
 * a shared promise settles identically for every awaiter. Deduplicate the
 * request, never the verdict (`docs/learnings/2026-08-01-deduped-access-check-
 * must-refresh-recovery-token.md`). Cleared as soon as it settles, so a
 * terminal result is never replayed to a later, unrelated caller.
 */
let inFlightRefresh: Promise<void> | null = null;

/** Silent renew. The cookie is the only credential; the BFF returns the stable session CSRF token. */
export async function refreshSession(): Promise<void> {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function performRefresh(): Promise<void> {
  let r: Response;
  try {
    r = await fetch(`${BASE}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: csrfHeaders(),
    });
  } catch (cause) {
    // The request never landed, so the server said nothing about the session.
    throw new RefreshSessionError(
      "transient",
      0,
      `refreshSession: network error (${String(cause)})`,
    );
  }
  if (!r.ok) {
    // Only a 401 is the BFF rejecting the session. A 403 is CsrfGuard telling
    // us the token is stale — recoverable — and clearing it here would strip
    // the header from every later write for no reason.
    if (r.status === 401) setCsrfToken(null);
    throw new RefreshSessionError(
      r.status === 401 ? "terminal" : "transient",
      r.status,
      `refreshSession: ${r.status} ${r.statusText}`,
    );
  }
  const body = (await r.json()) as { csrfToken: string };
  if (typeof body.csrfToken === "string") setCsrfToken(body.csrfToken);
}

/**
 * RP-initiated logout. The BFF clears the cookie and returns Keycloak's logout
 * URL — ALWAYS one (PB-5461, CONTRACT §2). With a live session it carries the
 * id_token hint; without one Keycloak asks "Do you want to sign out?" itself,
 * so a sign-out after the app session expired still ends the Keycloak sign-in
 * instead of signing the person straight back in.
 *
 * A 403 is a stale CSRF token, not a refusal: the current token is read once
 * and the sign-out tried once more. The tab's kept sign-in page and the
 * one-tab sign-in lease (d21) are forgotten here, so every sign-out path
 * drops them.
 */
export async function logout(): Promise<{ url: string }> {
  forgetReturnTo();
  clearSignInLease();
  const post = (): Promise<Response> =>
    fetch(`${BASE}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: csrfHeaders(),
    });
  let r = await post();
  if (r.status === 403) {
    // Best effort: an unreadable token leaves the retry to answer for itself.
    await requestCsrfToken().catch(() => undefined);
    r = await post();
  }
  setCsrfToken(null);
  if (!r.ok) throw new Error(`logout: ${r.status} ${r.statusText}`);
  const body = (await r.json()) as { url?: unknown };
  if (typeof body.url !== "string" || body.url.length === 0) {
    throw new Error("logout: no URL in answer");
  }
  return { url: body.url };
}

/**
 * A ws-ticket that could not be minted. A caller that retries reads `status`
 * and `retryAfterSeconds`; the notification bell treats every failure alike.
 */
export class WsTicketError extends Error {
  /** HTTP status, or 0 when no answer arrived (network, aborted). */
  readonly status: number;
  /** The server's `Retry-After`, in seconds, when it sent one. */
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    retryAfterSeconds: number | null,
    message: string,
  ) {
    super(message);
    this.name = "WsTicketError";
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Short-lived ticket for WS upgrade to services/collab. Single-use. A caller
 * may pass `signal` to give up; an abort, before the answer or while it is
 * read, is a `WsTicketError` with status 0 (no answer).
 */
export async function mintWsTicket(
  options: { readonly signal?: AbortSignal } = {},
): Promise<{
  ticket: string;
  expiresAt: number;
}> {
  const { signal } = options;
  let r: Response;
  try {
    r = await fetch(`${BASE}/api/auth/ws-ticket`, {
      method: "POST",
      credentials: "include",
      headers: csrfHeaders(),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw new WsTicketError(
      0,
      null,
      `mintWsTicket: network error (${String(cause)})`,
    );
  }
  if (!r.ok) {
    throw new WsTicketError(
      r.status,
      retryAfterSeconds(r.headers?.get("Retry-After") ?? null),
      `mintWsTicket: ${r.status} ${r.statusText}`,
    );
  }
  try {
    return (await r.json()) as { ticket: string; expiresAt: number };
  } catch (cause) {
    if (!signal?.aborted) throw cause;
    throw new WsTicketError(
      0,
      null,
      `mintWsTicket: answer not read (${String(cause)})`,
    );
  }
}

/**
 * Mint a purpose-bound secure collaboration ticket. HTTP status is collapsed
 * into the bounded lifecycle outcome; no response body or user identity leaks
 * into browser telemetry/errors.
 */
export async function mintCollabWsTicket(
  request: CollabTicketRequestV1,
): Promise<SecureTicketOutcomeV1> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/api/auth/collab-ws-ticket`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...csrfHeaders(),
      },
      body: JSON.stringify(request),
    });
  } catch {
    return { kind: "retryable", reason: "authorization_unavailable" };
  }

  if (response.ok) {
    let body: { ticket?: unknown; expiresAt?: unknown };
    try {
      body = (await response.json()) as {
        ticket?: unknown;
        expiresAt?: unknown;
      };
    } catch {
      return { kind: "terminal", reason: "authorization_unavailable" };
    }
    if (
      typeof body.ticket === "string" &&
      body.ticket.length > 0 &&
      typeof body.expiresAt === "number" &&
      Number.isSafeInteger(body.expiresAt) &&
      body.expiresAt > 0
    ) {
      return {
        kind: "issued",
        ticket: body.ticket,
        expiresAt: body.expiresAt,
      };
    }
    return { kind: "terminal", reason: "authorization_unavailable" };
  }

  if (
    response.status === 429 ||
    response.status === 502 ||
    response.status === 503 ||
    response.status === 504
  ) {
    const retryAfterMs = retryAfterMilliseconds(
      response.headers.get("Retry-After"),
    );
    return {
      kind: "retryable",
      reason: "authorization_unavailable",
      ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
    };
  }
  return { kind: "terminal", reason: "authorization_unavailable" };
}

/**
 * Best-effort, identity-free secure lifecycle telemetry. The lifecycle itself
 * is never allowed to wait for or fail because of this diagnostic endpoint.
 */
export function reportCollabClientEvent(
  event: SecureCollabLifecycleEventV1,
): void {
  void fetch(`${BASE}/api/auth/collab-client-events`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...csrfHeaders(),
    },
    body: JSON.stringify(event),
    keepalive: true,
  }).catch(() => undefined);
}

function retryAfterSeconds(value: string | null): number | null {
  if (value === null || !/^[0-9]+$/.test(value.trim())) return null;
  const seconds = Number(value.trim());
  return Number.isSafeInteger(seconds) ? seconds : null;
}

function retryAfterMilliseconds(value: string | null): number | undefined {
  if (value === null || !/^[1-9][0-9]*$/.test(value)) return undefined;
  const milliseconds = Number(value) * 1_000;
  return Number.isSafeInteger(milliseconds) ? milliseconds : undefined;
}

/**
 * Returns the headers to include on every state-changing request. Other
 * SPA code (the API client) imports this to plug into its own fetch
 * pipeline, so CSRF tokens flow uniformly.
 */
export function csrfHeaders(): Record<string, string> {
  return csrfToken ? { "X-CSRF-Token": csrfToken } : {};
}
